// Season handling for football-data.org standings requests.
//
// Without an explicit ?season= parameter the API keeps returning the previous
// season's final table even after the new season's metadata has flipped over.
// The season therefore always travels with the request, sourced from the
// `leagues` row so a season rollover stays a pure DB change.
//
// The proxy in server.js and the Edge Function in
// supabase/functions/_shared/league-data.ts mirror this rule — they run in
// separate runtimes (Node, Deno) and cannot import this module.

const SEASON_PATTERN = /^\d{4}$/;

/**
 * Normalise a season value to a four-digit string, or null if it is missing
 * or malformed. The season is the starting year, e.g. "2026" for 2026/27.
 */
export function sanitizeSeason(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const candidate = String(value);
  return SEASON_PATTERN.test(candidate) ? candidate : null;
}

/**
 * Build a football-data.org standings URL for a competition, appending the
 * season only when it is valid.
 */
export function buildFootballDataStandingsUrl(
  baseUrl: string,
  competitionCode: string,
  season: unknown,
): string {
  const base = baseUrl.replace(/\/+$/, "");
  const url = `${base}/${competitionCode}/standings`;
  const validSeason = sanitizeSeason(season);
  return validSeason ? `${url}?season=${validSeason}` : url;
}
